import type { BlinkEvent } from "../core/types.ts";

/** Bars kept on screen. Beyond this they are thinner than the gaps between. */
const MAX_BARS = 44;

/**
 * Floor for the vertical scale, in milliseconds.
 *
 * Without it the first blink of a session is by definition the longest one and
 * fills the strip, so every session opens looking identical no matter how it
 * goes. Holding the scale at a normal blink's length until something genuinely
 * exceeds it means a full-height bar always means "longer than a blink".
 */
const SCALE_FLOOR_MS = 300;

interface Bar {
  el: HTMLElement;
  duration: number;
}

/**
 * A strip of how long the eyes have been shut, one bar per closure.
 *
 * The suppression percentage it replaces was technically live and practically
 * static — it sat near zero through a normal session and only moved once you
 * had already noticed you were not blinking, which is too late to be feedback.
 * Duration is the opposite: every blink writes a bar, the long ones stand up
 * out of the row on their own, and the shape of the last forty is readable at a
 * glance without reading a single number.
 *
 * Deliberately faint. It sits over your own face during a session and its job
 * is to be noticed in peripheral vision, not to compete with the video.
 */
export class ClosureHistory {
  #bars: Bar[] = [];
  #scale = SCALE_FLOOR_MS;

  constructor(private readonly root: HTMLElement) {}

  add(event: BlinkEvent): void {
    const el = document.createElement("i");
    el.className = `closure-bar${event.kind === "partial" ? " partial" : ""}`;
    this.root.append(el);
    this.#bars.push({ el, duration: event.duration });

    while (this.#bars.length > MAX_BARS) this.#bars.shift()?.el.remove();

    // A new maximum rescales everything, so the strip stays comparable across
    // its own history rather than only against the most recent arrival.
    const next = Math.max(SCALE_FLOOR_MS, ...this.#bars.map((b) => b.duration));
    if (next !== this.#scale) {
      this.#scale = next;
      for (const bar of this.#bars) this.#paint(bar);
    } else {
      this.#paint(this.#bars[this.#bars.length - 1] as Bar);
    }
  }

  #paint(bar: Bar): void {
    // A visible floor, so a very short closure is still a mark rather than a
    // gap indistinguishable from no blink at all.
    const height = 12 + 88 * Math.min(1, bar.duration / this.#scale);
    bar.el.style.height = `${height.toFixed(1)}%`;
  }

  clear(): void {
    this.#bars = [];
    this.#scale = SCALE_FLOOR_MS;
    this.root.replaceChildren();
  }
}
